import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Paper,
} from "@mui/material";
import { useAddNewSite } from "../../utils/api/addNewSite.ts";
import { usePortfolios } from "../../utils/api/usePortfolios.ts";
import { useAuth } from "../../context/auth.context.tsx";
import { useNotification } from "../../context/notification.context.tsx";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import CustomButton from "../Basics/CustomButton/CustomButton.tsx";
import {
  validateSiteForm,
  parseBackendValidationErrors,
} from "../../utils/siteFormUtils.ts";

const REQUIRED_FIELDS = [
  "sam_id",
  "name",
  "portfolio",
  "address",
  "latitude",
  "longitude",
  "dc_kw",
  "ac_kw",
] as const;
const NUMERIC_FIELDS = ["dc_kw", "ac_kw", "latitude", "longitude"];

interface AddNewSiteModalProps {
  open: boolean;
  onClose: () => void;
}

const initialFormData = {
  sam_id: "",
  name: "",
  city: "",
  state: "",
  docket_id: "",
  asset_scale: "",
  address: "",
  portfolio: "",
  dc_kw: "",
  ac_kw: "",
  dc_source: "",
  ac_source: "",
  latitude: "",
  longitude: "",
};

const AddNewSiteModal: React.FC<AddNewSiteModalProps> = ({ open, onClose }) => {
  const { mutate: addSite, isPending } = useAddNewSite();
  const { session } = useAuth();
  const { addNotification } = useNotification();
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { data: PortfoliosData } = usePortfolios(session);

  useEffect(() => {
    if (!open) setFormErrors({});
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    const errors = validateSiteForm(
      formData,
      REQUIRED_FIELDS,
      NUMERIC_FIELDS
    );
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const payload = { ...formData };
    payload.portfolio = `${process.env.REACT_APP_API_BASE_URL}portfolios/${formData.portfolio}/`;
    payload.dc_source = (payload.dc_source || "").replace(/ /g, "_");
    payload.ac_source = (payload.ac_source || "").replace(/ /g, "_");

    addSite(payload, {
      onSuccess: () => {
        addNotification("The site was added successfully", true);
        setFormData(initialFormData);
        setFormErrors({});
        onClose();
      },
      onError: (error) => {
        const errs = parseBackendValidationErrors(error);
        if (errs) {
          setFormErrors(errs);
        } else {
          console.error("Failed to add site", error);
          addNotification("Failed to add the site", false);
        }
      },
    });
  };

  const sourceOptions = [
    "Early stage projects",
    "Asset Registry",
    "COD Tracker",
    "SSI Constr Early Stage",
    "SSI Operational registry",
    "M&A operations",
    "utility fleet",
    "TransAlta operations",
    "ECHO Residential",
    "ASD Residential",
    "Deriva Energy",
    "X-ELIO",
    "BEP US",
  ];

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 500,
    maxHeight: "80vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Paper
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingX: 4,
            paddingY: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              marginTop: 1,
              fontWeight: "bold",
            }}
          >
            ADD A NEW SITE
          </Typography>
          <IconButton onClick={onClose}>
            <HighlightOffOutlinedIcon />
          </IconButton>
        </Paper>

        <Box
          component="form"
          sx={{
            mt: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 4,
          }}
        >
          <TextField
            label="SAM ID *"
            variant="outlined"
            fullWidth
            name="sam_id"
            value={formData.sam_id}
            onChange={handleChange}
            error={!!formErrors.sam_id}
            helperText={formErrors.sam_id}
          />
          <TextField
            label="Name *"
            variant="outlined"
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
          />
          <TextField
            label="City"
            variant="outlined"
            fullWidth
            name="city"
            value={formData.city}
            onChange={handleChange}
            error={!!formErrors.city}
            helperText={formErrors.city}
          />
          <TextField
            label="State"
            variant="outlined"
            fullWidth
            name="state"
            value={formData.state}
            onChange={handleChange}
            error={!!formErrors.state}
            helperText={formErrors.state}
          />
          <TextField
            label="QF Docket ID"
            variant="outlined"
            fullWidth
            name="docket_id"
            value={formData.docket_id}
            onChange={handleChange}
            error={!!formErrors.docket_id}
            helperText={formErrors.docket_id}
          />
          <TextField
            select
            label="Asset Scale"
            variant="outlined"
            fullWidth
            name="asset_scale"
            value={formData.asset_scale}
            onChange={handleChange}
          >
            <MenuItem value="RES">Residential</MenuItem>
            <MenuItem value="COM">Commercial</MenuItem>
            <MenuItem value="UTL">Utility</MenuItem>
          </TextField>
          <TextField
            label="Address *"
            variant="outlined"
            fullWidth
            name="address"
            value={formData.address}
            onChange={handleChange}
            error={!!formErrors.address}
            helperText={formErrors.address}
          />
          <TextField
            select
            label="Portfolio *"
            variant="outlined"
            fullWidth
            name="portfolio"
            value={formData.portfolio}
            onChange={handleChange}
            error={!!formErrors.portfolio}
            helperText={formErrors.portfolio}
          >
            {PortfoliosData?.map((portfolio: { id: number; name: string }) => (
              <MenuItem key={portfolio.id} value={portfolio.id}>
                {portfolio.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="DC kW *"
            variant="outlined"
            fullWidth
            name="dc_kw"
            value={formData.dc_kw}
            onChange={handleChange}
            error={!!formErrors.dc_kw}
            helperText={formErrors.dc_kw}
          />
          <TextField
            label="AC kW *"
            variant="outlined"
            fullWidth
            name="ac_kw"
            value={formData.ac_kw}
            onChange={handleChange}
            error={!!formErrors.ac_kw}
            helperText={formErrors.ac_kw}
          />
          <TextField
            select
            label="DC Source"
            variant="outlined"
            fullWidth
            name="dc_source"
            value={formData.dc_source}
            onChange={handleChange}
            error={!!formErrors.dc_source}
            helperText={formErrors.dc_source}
          >
            {sourceOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="AC Source"
            variant="outlined"
            fullWidth
            name="ac_source"
            value={formData.ac_source}
            onChange={handleChange}
            error={!!formErrors.ac_source}
            helperText={formErrors.ac_source}
          >
            {sourceOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Latitude *"
            variant="outlined"
            fullWidth
            name="latitude"
            value={formData.latitude}
            onChange={handleChange}
            error={!!formErrors.latitude}
            helperText={formErrors.latitude}
          />
          <TextField
            label="Longitude *"
            variant="outlined"
            fullWidth
            name="longitude"
            value={formData.longitude}
            onChange={handleChange}
            error={!!formErrors.longitude}
            helperText={formErrors.longitude}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <CustomButton
              disabled={isPending}
              sx={{
                paddingY: 0.5,
              }}
              variant="outlined"
              color="secondary"
              label=" Cancel"
              onClick={onClose}
            />

            <CustomButton
              disabled={isPending}
              sx={{
                paddingY: 0.5,
              }}
              variant="contained"
              color="primary"
              label="Create Site"
              onClick={handleSubmit}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddNewSiteModal;
